export interface VersionInfo {
  app: string;
}

export interface UpdateChecker {
  /** Check once for updates. Returns true if an update is available. */
  checkOnce(): Promise<boolean>;
  /** True if a new app version is available. */
  hasAppUpdate(): boolean;
  /** Triggers SW update. Resolves when new SW is activated and ready. */
  applyAppUpdate(): Promise<void>;
}

export function createUpdateChecker(): UpdateChecker {
  let localVersions: VersionInfo | null = null;
  let remoteVersions: VersionInfo | null = null;

  async function loadLocalVersions() {
    if (localVersions) return;
    try {
      // Plain fetch — SW intercepts cache-first, returning the installed version.
      // If nothing is cached yet, network response is used (local == remote, no false positive).
      const res = await fetch('./versions.json');
      if (!res.ok) return;
      const vData = await res.json();
      localVersions = { app: vData?.app ?? '' };
    } catch {
      // Can't determine local versions — skip update check
    }
  }

  return {
    async checkOnce(): Promise<boolean> {
      await loadLocalVersions();
      if (!localVersions) return false;

      try {
        const res = await fetch('./versions.json?_t=' + Date.now(), { cache: 'no-store' });
        if (!res.ok) return false;
        remoteVersions = await res.json();
        return remoteVersions!.app !== localVersions.app;
      } catch {
        return false;
      }
    },

    hasAppUpdate() {
      if (!localVersions || !remoteVersions) return false;
      return remoteVersions.app !== localVersions.app;
    },

    applyAppUpdate(): Promise<void> {
      if (!('serviceWorker' in navigator)) {
        return Promise.reject(new Error('No service worker support'));
      }

      return new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('SW update timed out')), 15000);

        navigator.serviceWorker.getRegistration().then(reg => {
          if (!reg) { clearTimeout(timeout); reject(new Error('No SW registration')); return; }

          const onActivated = (sw: ServiceWorker) => {
            if (sw.state === 'activated') { clearTimeout(timeout); resolve(); return; }
            sw.addEventListener('statechange', () => {
              if (sw.state === 'activated') { clearTimeout(timeout); resolve(); }
            });
          };

          if (reg.waiting) { clearTimeout(timeout); resolve(); return; }

          if (reg.installing) { onActivated(reg.installing); }

          reg.addEventListener('updatefound', () => {
            if (reg.installing) onActivated(reg.installing);
          });

          reg.update();
        }).catch(err => { clearTimeout(timeout); reject(err); });
      });
    },
  };
}
